import { Component, Input, OnInit, ViewChild, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { PanelsDirective } from './panels.directive';
import { PanelsInterfaceComponent } from './panels-interface.component';
import { PanelsNotesComponent } from './notes/notes.component';
import { PanelsFormComponent } from './form/form.component';
import { PanelsImagesComponent } from './images/images.component';
import { PanelsMapComponent } from './map/map.component';
import { PanelsCommentsComponent } from './comments/comments.component';
import { PanelsTasksComponent } from './legacy-tasks/tasks.component';
import { PanelsFilesComponent } from './files/files.component';
import { PanelsDatesComponent } from './dates/dates.component';
import { PanelsSummaryComponent } from './summary/summary.component';
import { PanelsContactsComponent } from './contacts/contacts.component';
import { PanelsOffersComponent } from './offers/offers.component';
import { PanelsMailboxComponent } from './mailbox/mailbox.component';
import { PanelsDispositionsComponent } from './dispositions/dispositions.component';
import { PanelsNearbyComponent } from './nearby/nearby.component';
import { AccountService, AuthService } from 'app/core';
import { PanelsUnitsTableComponent } from './units-table/units-table.component';
import { PanelsPortfolioDetailComponent } from './portfolio-detail/portfolio-detail.component';
import { TableComponent } from './table/table.component';
import { PanelsActivityComponent } from '@shared/components/panels/activity/activity.component';
import { PanelsScenariosComponent } from './scenarios/scenarios.component';
import { Project } from 'app/modules/projects/projects.types';
import { PanelMatrixComponent } from 'app/shared/components/panels/matrix/matrix.component';
import { RelatedToType } from 'app/shared/enums';
import { PanelComponentBase, PanelDef } from 'app/shared/components/panels/panels.types';
import { getAllowedFields } from 'app/shared/functions';
import { PanelsChartComponent } from './chart/chart.component';
import { Permission } from 'app/core/enums';

const panelComponents = {
  notes: PanelsNotesComponent,
  form: PanelsFormComponent,
  images: PanelsImagesComponent,
  map: PanelsMapComponent,
  comments: PanelsCommentsComponent,
  tasks: PanelsTasksComponent,
  files: PanelsFilesComponent,
  dates: PanelsDatesComponent,
  summary: PanelsSummaryComponent,
  contacts: PanelsContactsComponent,
  offers: PanelsOffersComponent,
  dispositions: PanelsDispositionsComponent,
  mailbox: PanelsMailboxComponent,
  nearby: PanelsNearbyComponent,
  rent_roll: PanelsUnitsTableComponent,
  portfolio_detail: PanelsPortfolioDetailComponent,
  table: TableComponent,
  activity: PanelsActivityComponent,
  chart: PanelsChartComponent,
  scenarios: PanelsScenariosComponent,
  matrix: PanelMatrixComponent,
  roles: PanelsFormComponent,
};

type PanelType = keyof typeof panelComponents;

@Component({
  selector: 'project-panel',
  templateUrl: './panels.component.html',
  styleUrls: ['./panels.component.scss'],
})
export class PanelsComponent implements OnInit, OnChanges {
  @Input() panelDef: PanelDef<any>;
  @Input() project: any;
  @Input() account: any;
  @Input() data: any;
  @Input() readonly = false;
  @Input() canEditPanelDef = true;
  @Output() onUpdate: EventEmitter<string> = new EventEmitter();
  @Output() onEditPanelDef: EventEmitter<any> = new EventEmitter();
  @Output() silentPanelDefUpdated: EventEmitter<any> = new EventEmitter();
  @Output() reloadAllPanelsOfTypes: EventEmitter<string[]> = new EventEmitter();

  @ViewChild(PanelsDirective, { static: true }) panelHost: PanelsDirective;

  instance: any;

  private nextGen: PanelType[] = ['matrix', 'chart', 'activity'];
  private readonlyExceptions: PanelType[] = [
    'comments',
    'map',
    'roles',
    'contacts',
    'files',
    'images',
    'dates',
    'offers',
    'dispositions',
  ];

  private get isNextGen(): boolean {
    return this.nextGen.includes(this.panelDef.type as PanelType);
  }

  private get canEditLayout(): boolean {
    return this.authService.hasPermission(Permission.PROJECT_DASHBOARD_EDIT_LAYOUT);
  }

  constructor(
    private readonly accountService: AccountService,
    private readonly authService: AuthService,
  ) {
    if (this.accountService.getCurrentAccount()?.subdomain !== 'platformv') {
      delete panelComponents.nearby;
    }
  }

  ngOnInit(): void {
    this.loadComponent();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.project && changes.project.previousValue && changes.project.currentValue) {
      if (this.reloadComponent(changes.project.previousValue, changes.project.currentValue)) {
        this.loadComponent();
      }
    }
  }

  private reloadComponent(previous: Project, current: Project): boolean {
    return this.projectIdChanged(previous, current) || this.archivedProjectStatusChanged(previous, current);
  }

  private projectIdChanged(previous: Project, current: Project): boolean {
    return previous.id !== current.id;
  }

  private archivedProjectStatusChanged(previous: Project, current: Project): boolean {
    return this.panelDef.type === 'form' && previous.archived_at !== current.archived_at;
  }

  loadComponent(): void {
    if (this.panelDef.type in panelComponents) {
      const viewContainerRef = this.panelHost.viewContainerRef;
      viewContainerRef.clear();
      if (!this.isNextGen) {
        const componentRef = viewContainerRef.createComponent(panelComponents[this.panelDef.type]);
        const componentInstance = <PanelsInterfaceComponent>componentRef.instance;
        componentInstance.project = this.project;
        componentInstance.account = this.account;
        componentInstance.panelDef = this.panelDef;
        componentInstance.onUpdate = this.onUpdate;
        componentInstance.onEditPanelDef = this.onEditPanelDef;
        componentInstance.silentPanelDefUpdated = this.silentPanelDefUpdated;
        componentInstance.relatedToType = RelatedToType.Project;
        componentInstance.data = this.data;
        if (this.readonlyExceptions.includes(this.panelDef.type as PanelType)) {
          componentInstance.readonly = this.readonly;
        } else {
          componentInstance.readonly = !this.canEditLayout ? true : this.readonly;
        }

        componentInstance.canEditPanelDef = !this.canEditLayout ? false : this.canEditPanelDef;
        componentInstance.reloadAllPanelsOfTypes = this.reloadAllPanelsOfTypes;
        this.instance = componentInstance;
      } else {
        const componentRef = viewContainerRef.createComponent(panelComponents[this.panelDef.type]);
        const componentInstance = <PanelComponentBase<any>>componentRef.instance;
        componentInstance.panelDef = this.panelDef;
        componentInstance.data = this.data;
        componentInstance.fieldDefs = getAllowedFields(
          this.data.fieldDefs,
          RelatedToType.Project,
          false,
          false,
          this.panelDef.type === 'matrix',
        );
        componentInstance.model = this.project;
        componentInstance.readonly = !this.canEditLayout;
        componentInstance.relatedToType = RelatedToType.Project;
        componentInstance.panelDefUpdated = this.onEditPanelDef;
        componentInstance.silentPanelDefUpdated = this.silentPanelDefUpdated;
        componentInstance.dataUpdated = this.onUpdate;
        this.instance = componentInstance;
      }
    }
  }
}
